import React, { memo, useCallback } from "react";
import { logger } from "@origin-digital/reporting-client";

import { useFormContext } from "react-hook-form";

import { RadioGroupInput, RouterActions, StyledHeading } from "@/components";
import { StyledQuestion } from "@/components/StyledQuestion";
import { useFieldValidation } from "@/hooks";
import { isError } from "@/utils";

import { usagePattern } from "./content.json";
import { useUsagePattern } from "./utils/useUsagePattern";
import { isBatteryJourney } from "@/features/SolarForm/utils/formJourney";

export const UsagePattern = memo(
  ({
    backRoute,
    nextRoute,
    batteryBackRoute,
  }: {
    backRoute: () => Promise<string>;
    nextRoute: () => Promise<string>;
    batteryBackRoute: () => Promise<string>;
  }) => {
    const { getValues } = useFormContext();
    const { isValid } = useFieldValidation({ fields: ["usagePattern"] });
    const { fetchUsage } = useUsagePattern();

    const onNext = async () => {
      // If 'degradedExperience' is true, redirect to the degraded experience route
      if (getValues("degradedExperience")) {
        logger.info("Redirecting to degraded experience route");
        return true;
      }

      // Validate the form field for 'usagePattern', this makes api call for both solar and solar+battery journeys
      const isFieldValid = await isValid();
      if (!isFieldValid) return false;

      const billAmount = Number.parseFloat(
        getValues("averageBill").replaceAll(",", "")
      );

      const usage = await fetchUsage(
        billAmount,
        getValues("billDuration"),
        getValues("usagePattern")
      );
      if (isError(usage)) return false;

      return true;
    };

    const systemType = getValues("systemType");

    const handleBackRoute = useCallback(() => {
      return isBatteryJourney(systemType) ? batteryBackRoute() : backRoute();
    }, [systemType]);

    const handleOnNext = useCallback(() => {
      return isBatteryJourney(systemType) ? Promise.resolve(true) : onNext();
    }, [systemType]);

    return (
      <StyledQuestion>
        <div>
          {/* Heading and radio button input for the usage pattern question */}
          <StyledHeading>{usagePattern.heading}</StyledHeading>
          <RadioGroupInput
            data-id={usagePattern.id}
            name="usagePattern"
            label=""
            options={usagePattern.option}
          />
        </div>

        {/* Navigation actions */}
        <RouterActions
          onBackRoute={handleBackRoute}
          onNextRoute={nextRoute}
          onNext={handleOnNext}
        />
      </StyledQuestion>
    );
  }
);
